import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Heart, Quote } from "lucide-react";
import { FOUNDER_PHOTO } from "@/lib/assets";

const Highlight = ({ children }) => (
  <p className="font-display text-2xl sm:text-3xl text-[#1F4D2E] leading-snug border-l-4 border-[#E0A526] pl-6 my-10">{children}</p>
);

const H = ({ children }) => (
  <h2 className="font-display text-3xl sm:text-4xl font-semibold text-[#1F4D2E] mt-14 mb-5 tracking-tight">{children}</h2>
);

const P = ({ children }) => <p className="text-[#4F6B54] leading-relaxed text-[17px] mb-4">{children}</p>;

export default function FounderPage() {
  return (
    <div data-testid="founder-page">
      {/* HERO */}
      <section className="bg-[#1F4D2E] text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-4 order-1 flex justify-center md:justify-start">
            <img src={FOUNDER_PHOTO} alt="Amelework &ldquo;Amilly&rdquo; Kassahun, Founder of Refugium Ethiopia" data-testid="founder-photo" className="w-52 h-52 rounded-full object-cover object-top border-4 border-[#E0A526]/40 shadow-xl mx-auto" />
          </div>
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="md:col-span-8 order-2">
            <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#E0A526] mb-3">Meet Our Founder</p>
            <h1 className="font-display text-5xl sm:text-6xl font-bold tracking-tight leading-[0.95]">Amelework &ldquo;Amilly&rdquo; Kassahun</h1>
            <p className="text-[#E0A526] font-medium mt-3">Founder, Refugium Ethiopia · Addis Ababa, Ethiopia</p>
            <p className="text-lg text-[#E7EDE2]/90 mt-6 leading-relaxed max-w-2xl">Her childhood began with abandonment. Her journey became one of survival. Today, she is determined to help give vulnerable children the opportunities that changed her own life.</p>
          </motion.div>
        </div>
      </section>

      {/* STORY */}
      <article className="max-w-3xl mx-auto px-6 lg:px-10 py-16">
        <H>My Story</H>
        <P>My name is Amelework Kassahun, but most people know me as Amilly. I was born in Addis Ababa, Ethiopia, the city I still call home.</P>
        <P>I was abandoned near a river when I was only 40 days old. I was found and rescued by people passing by and eventually raised by an older woman I came to call my grandmother.</P>
        <P>My childhood was difficult. I experienced abuse, and by the age of five, I was selling tissues and gum around bars and cafés. There were nights when I slept outside and faced situations no child should have to experience.</P>
        <P>But my story did not end there.</P>

        <H>Someone Opened a Door</H>
        <P>People in my community saw my situation and chose to help. At about 12 years old, I entered Hope for Youth Ethiopia. That opportunity changed the direction of my life.</P>
        <P>I was given the chance to learn, grow, strengthen my faith and begin imagining a future beyond survival.</P>
        <P>I later studied sociology, volunteered with children, taught Sunday school and gained professional experience in sales and management. Today, I also run a small grocery store in Addis Ababa, where I continue trying to help people in my community whenever I can.</P>
        <Highlight>&ldquo;I know what one opportunity can do because someone once gave one to me.&rdquo;</Highlight>

        <H>The Inspiration of Mercy</H>
        <P>One of the most important people in my childhood was my best friend, Mercy.</P>
        <P>Mercy and I grew up facing many of the same struggles. As young girls, we sold tissues and gum together on the streets of Addis Ababa. Mercy worked hard to help her mother and younger brother through incredibly difficult circumstances.</P>
        <P>Mercy passed away when we were only eight years old. Losing my best friend had a lasting impact on my life, and I have carried her memory with me ever since.</P>
        <Highlight>&ldquo;Mercy deserved a safe place to call home.&rdquo;</Highlight>
        <P>Her life and her memory are part of the inspiration behind Refugium Ethiopia.</P>
        <Link to="/story" data-testid="founder-read-mercy" className="inline-flex items-center gap-2 bg-[#1F4D2E] text-white rounded-full px-7 py-3.5 font-semibold hover:bg-[#C1272D] transition-colors">Read Mercy's Story <ArrowRight size={17} /></Link>

        <H>Why I Founded Refugium Ethiopia</H>
        <P>Today, when I see vulnerable children in Ethiopia, I see pieces of my own childhood. But I also see possibility.</P>
        <P>I know what can happen when someone cares enough to open a door for a child. Education, support, faith and opportunity helped change the direction of my life. That is why I founded Refugium Ethiopia.</P>
        <P>My dream is to create a safe and loving environment where vulnerable girls can be protected, continue their education, receive support and have the opportunity to build a different future. I want them to grow into educated, confident and compassionate women who can one day reach back and help others too.</P>
        <Highlight>&ldquo;Someone opened a door for me. Now I want to hold that door open for them.&rdquo;</Highlight>
      </article>

      {/* MY LIFE CHANGED */}
      <section className="bg-[#C1272D] text-white text-center py-20 px-6">
        <p className="font-display text-4xl sm:text-6xl font-bold leading-[1.05]">My life changed.<br />Why can't theirs?</p>
      </section>

      {/* FAITH */}
      <section className="max-w-3xl mx-auto px-6 lg:px-10 py-20 text-center">
        <Quote size={32} className="text-[#E0A526] mx-auto mb-5" />
        <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#C1272D] mb-4">The scripture that guides me</p>
        <p className="font-display text-2xl sm:text-3xl text-[#1F4D2E] leading-snug">&ldquo;I will not leave you comfortless: I will come to you.&rdquo;</p>
        <p className="text-[#4F6B54] mt-3 font-medium">John 14:18</p>
        <p className="text-[#4F6B54] mt-8">Amelework &ldquo;Amilly&rdquo; Kassahun<br /><span className="text-sm text-[#4F6B54]/70">Founder, Refugium Ethiopia</span></p>
      </section>

      {/* CTA */}
      <section className="bg-[#1F4D2E] text-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-10 py-16 text-center">
          <h2 className="font-display text-3xl sm:text-4xl font-semibold">Help us open the door for another child.</h2>
          <div className="flex flex-wrap gap-4 justify-center mt-8">
            <Link to="/projects" data-testid="founder-cta-home" className="bg-[#C1272D] text-white rounded-full px-8 py-4 font-semibold hover:bg-[#9E1F24] transition-colors inline-flex items-center gap-2"><Heart size={18} fill="white" /> Help build our first home</Link>
            <Link to="/join" data-testid="founder-cta-involved" className="border border-white/40 rounded-full px-8 py-4 font-semibold hover:bg-white/10 transition-colors inline-flex items-center gap-2">Get involved <ArrowRight size={18} /></Link>
          </div>
        </div>
      </section>
    </div>
  );
}
